"use client";
import { useMemo, useState } from "react";
import { ShieldAlert } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useRooms } from "@/components/calculators/room-context";
import {
  toNum,
  fmt,
  DEHU_CATALOG,
  NAM_UNITS,
} from "@/components/calculators/calc-constants";
import { NumField, ResultPanel } from "@/components/calculators/calc-ui";

export function MoldRemediationTab() {
  const { summary, hasRooms } = useRooms();

  /* Pre-fill from room summary, allow manual override */
  const [manualL, setManualL] = useState("");
  const [manualW, setManualW] = useState("");
  const [manualH, setManualH] = useState("");
  const [targetAch, setTargetAch] = useState("6");
  const [namCfm, setNamCfm] = useState("500");
  const [dehuId, setDehuId] = useState(DEHU_CATALOG[6].id);

  const dehu = DEHU_CATALOG.find((d) => d.id === dehuId) ?? DEHU_CATALOG[0];

  /* Effective volume — manual overrides rooms */
  const effectiveVolume = useMemo(() => {
    const L = toNum(manualL), W = toNum(manualW), H = toNum(manualH);
    if (L > 0 && W > 0 && H > 0) return L * W * H;
    return summary.totalVolumeFt3;
  }, [manualL, manualW, manualH, summary.totalVolumeFt3]);

  const effectiveArea = useMemo(() => {
    const L = toNum(manualL), W = toNum(manualW);
    if (L > 0 && W > 0) return L * W;
    return summary.totalFloorSqFt;
  }, [manualL, manualW, summary.totalFloorSqFt]);

  /* Containment ACH */
  const ach = toNum(targetAch) || 6;
  const reqCfm = (effectiveVolume * ach) / 60;
  const namCount = toNum(namCfm) > 0 ? Math.ceil(reqCfm / toNum(namCfm)) : 0;

  /* HEPA sandwich */
  const preHepa = effectiveArea > 0 ? Math.ceil(effectiveArea / 500) : 0;
  const workHepa = effectiveArea > 0 ? Math.ceil(effectiveArea / 250) : 0;
  const postHepa = effectiveArea > 0 ? Math.ceil(effectiveArea / 500) : 0;

  /* Make-up air — industry rule of thumb is 400 fpm through a free
   * opening. Required opening area in sq inches:
   *   opening_sqft = CFM / 400 fpm
   *   opening_sq_in = opening_sqft × 144 = CFM × 144 / 400 = CFM × 0.36
   * Previously this code divided by 400 directly (omitting the ×144),
   * which under-sized every recommended opening by 144×. A 1000 CFM
   * exhaust now correctly recommends ~360 sq in (≈19"×19") instead of
   * the previous nonsensical 2.5 sq in. */
  const totalNamCfm = namCount * toNum(namCfm);
  const openingSqIn = (totalNamCfm * 144) / 400;
  const openingW = Math.round(Math.sqrt(openingSqIn * 2));
  const openingH = openingW > 0 ? Math.round(openingSqIn / openingW) : 0;

  /* Containment dehu — Class 3 factor (0.3) per IICRC S520 */
  const dehuPintsNeeded = effectiveVolume * 0.3;
  const dehuCount = dehu.aham > 0 && dehuPintsNeeded > 0 ? Math.ceil(dehuPintsNeeded / dehu.aham) : 0;

  const panelStyle = { background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "20px 22px", marginBottom: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.55)" };
  const sectionTitle = { fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#D4A84B", marginBottom: 14 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* Dimension source */}
      <div style={panelStyle}>
        <p style={sectionTitle}>Containment Dimensions</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.70)", marginBottom: 12 }}>
          {hasRooms
            ? `Using room summary: ${fmt(effectiveVolume)} ft³ · ${fmt(effectiveArea)} ft². Enter manual dimensions below to override.`
            : "No rooms added — enter containment dimensions manually."}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <NumField id="mr-l" label="Length (ft)" value={manualL} onChange={setManualL} placeholder="auto" />
          <NumField id="mr-w" label="Width (ft)" value={manualW} onChange={setManualW} placeholder="auto" />
          <NumField id="mr-h" label="Height (ft)" value={manualH} onChange={setManualH} placeholder="auto" />
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 20 }}>
          <div>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Volume</span>
            <p style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{effectiveVolume ? fmt(effectiveVolume) : "—"} ft³</p>
          </div>
          <div>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Area</span>
            <p style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{effectiveArea ? fmt(effectiveArea) : "—"} ft²</p>
          </div>
        </div>
      </div>

      {/* Section 1 — Containment ACH */}
      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <ShieldAlert size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>1 — Containment ACH (IICRC S520)</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="mr-ach" style={{ color: "rgba(255,255,255,0.85)" }}>Target ACH</Label>
              <Select value={targetAch} onValueChange={(v) => setTargetAch(v ?? "")}>
                <SelectTrigger id="mr-ach" className="h-10 w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 ACH — Level II (small area)</SelectItem>
                  <SelectItem value="6">6 ACH — Level III (mid containment)</SelectItem>
                  <SelectItem value="8">8 ACH — Level IV (large / sensitive)</SelectItem>
                  <SelectItem value="12">12 ACH — Critical / healthcare</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mr-nam" style={{ color: "rgba(255,255,255,0.85)" }}>Negative-air machine</Label>
              <Select value={namCfm} onValueChange={(v) => setNamCfm(v ?? "")}>
                <SelectTrigger id="mr-nam" className="h-10 w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NAM_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <ResultPanel
            title="NAMs Required"
            stat={namCount ? String(namCount) : "—"}
            unit="units"
            rows={[
              { label: "Containment volume", value: effectiveVolume ? `${fmt(effectiveVolume)} ft³` : "—" },
              { label: "Required airflow", value: reqCfm ? `${fmt(reqCfm)} CFM` : "—" },
              { label: "Target air changes", value: `${targetAch} ACH` },
            ]}
            footnote="Per IICRC S520 / ANSI Z9.5. Verify negative pressure −0.02 to −0.05 in WC with a manometer or smoke pencil after setup."
          />
        </div>
      </div>

      {/* Section 2 — HEPA Sandwich */}
      <div style={panelStyle}>
        <p style={sectionTitle}>2 — HEPA Sandwich Method</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.65, marginBottom: 14 }}>
          The HEPA sandwich uses three passes of air scrubbing: Pre-HEPA cleans the air before work begins; Work-HEPA runs continuously during remediation; Post-HEPA verifies clearance-level air quality. Sizing is based on containment floor area (ft²).
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {[
            { label: "Pre-HEPA", count: preHepa, rule: "1 per 500 ft²", desc: "Run 30 min before work starts" },
            { label: "Work HEPA", count: workHepa, rule: "1 per 250 ft²", desc: "Continuous during remediation" },
            { label: "Post-HEPA", count: postHepa, rule: "1 per 500 ft²", desc: "Run until clearance sample" },
          ].map((item) => (
            <div key={item.label} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em" }}>{item.label}</p>
              <p style={{ fontSize: 32, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums", margin: "6px 0" }}>{item.count || "—"}</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{item.rule}</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3 — Make-up Air */}
      <div style={panelStyle}>
        <p style={sectionTitle}>3 — Make-up Air Opening</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.65, marginBottom: 14 }}>
          Every cubic foot exhausted by NAMs must be replaced. Size the make-up air opening at 400 ft/min through a free opening: opening_sqft = totalNAM_CFM ÷ 400 fpm, then × 144 to express in square inches. For 1,000 CFM exhaust → 360 sq in opening (≈19″×19″).
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em" }}>Total NAM CFM</p>
            <p style={{ fontSize: 28, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums", margin: "6px 0" }}>{totalNamCfm ? fmt(totalNamCfm) : "—"}</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>CFM exhausted</p>
          </div>
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em" }}>Opening Area</p>
            <p style={{ fontSize: 28, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums", margin: "6px 0" }}>{openingSqIn ? fmt(openingSqIn, 1) : "—"}</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>square inches</p>
          </div>
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em" }}>Recommended Opening</p>
            <p style={{ fontSize: 28, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums", margin: "6px 0" }}>{openingW && openingH ? `${openingW}×${openingH}` : "—"}</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>inches (W × H)</p>
          </div>
        </div>
      </div>

      {/* Section 4 — Containment Dehu */}
      <div style={panelStyle}>
        <p style={sectionTitle}>4 — Containment Dehumidifier</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.65, marginBottom: 14 }}>
          Mold remediation containments use the Class 3 moisture load factor (0.30 pints/ft³/day per IICRC S520) to prevent humidity build-up inside the work area during remediation.
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="mr-dehu" style={{ color: "rgba(255,255,255,0.85)" }}>Dehumidifier</Label>
            <Select value={dehuId} onValueChange={(v) => setDehuId(v ?? "")}>
              <SelectTrigger id="mr-dehu" className="h-10 w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEHU_CATALOG.map((d) => <SelectItem key={d.id} value={d.id}>{d.label} — {d.aham} pts/day</SelectItem>)}
              </SelectContent>
            </Select>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.60)", marginTop: 6 }}>
              Load: {effectiveVolume ? fmt(dehuPintsNeeded) : "—"} pts/day (volume × 0.30)
            </p>
          </div>
          <ResultPanel
            title="Containment Dehus"
            stat={dehuCount ? String(dehuCount) : "—"}
            unit="units"
            rows={[
              { label: "Volume", value: effectiveVolume ? `${fmt(effectiveVolume)} ft³` : "—" },
              { label: "Load (Class 3 factor)", value: dehuPintsNeeded ? `${fmt(dehuPintsNeeded)} pts/day` : "—" },
              { label: "Unit AHAM", value: `${dehu.aham} pts/day` },
            ]}
            footnote="Class 3 drying factor per IICRC S520. Containment environments stay near saturation; maintain RH < 60% inside to inhibit mold re-growth during remediation."
          />
        </div>
      </div>
    </div>
  );
}
